import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getSubscriptionTiers,
  getUserSubscription,
  getUserTier,
  hasSetupFeePaid,
  getSubscriptionStatus,
  checkFeatureAccess,
  getUsageQuota,
  isUserAdmin,
  type SubscriptionTier,
  type UserSubscription,
  type UsageQuota,
} from '../services/subscriptionService';

export interface SubscriptionData {
  tiers: SubscriptionTier[];
  currentSubscription: UserSubscription | null;
  currentTier: string | null;
  hasSetupFee: boolean;
  subscriptionStatus: {
    subscription_tier: string | null;
    subscription_status: string | null;
    setup_fee_paid_at: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  } | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useSubscription(): SubscriptionData {
  const { user } = useAuth();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [hasSetupFee, setHasSetupFee] = useState<boolean>(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionData['subscriptionStatus']>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscriptionData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [tiersData, subscription, tier, setupFee, status] = await Promise.all([
        getSubscriptionTiers(),
        getUserSubscription(user.id),
        getUserTier(user.id),
        hasSetupFeePaid(user.id),
        getSubscriptionStatus(user.id),
      ]);

      setTiers(tiersData);
      setCurrentSubscription(subscription);
      setCurrentTier(tier);
      setHasSetupFee(setupFee);
      setSubscriptionStatus(status);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch subscription data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, [user]);

  return {
    tiers,
    currentSubscription,
    currentTier,
    hasSetupFee,
    subscriptionStatus,
    loading,
    error,
    refetch: fetchSubscriptionData,
  };
}

export interface FeatureAccessData {
  hasAccess: boolean;
  loading: boolean;
  error: Error | null;
  checkAccess: (featureKey: string) => Promise<boolean>;
}

export function useFeatureAccess(featureKey: string): FeatureAccessData {
  const { user, isAdmin } = useAuth();
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const checkAccess = async (key: string = featureKey): Promise<boolean> => {
    if (!user) {
      return false;
    }

    // ADMIN BYPASS: Admins have access to all features
    if (isAdmin) {
      setHasAccess(true);
      return true;
    }

    try {
      setLoading(true);
      setError(null);
      const access = await checkFeatureAccess(user.id, key);
      setHasAccess(access);
      return access;
    } catch (err) {
      console.error('Error checking feature access:', err);
      setError(err instanceof Error ? err : new Error('Failed to check feature access'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ADMIN BYPASS: Skip API call for admins
    if (isAdmin) {
      setHasAccess(true);
      setLoading(false);
      return;
    }

    checkAccess(featureKey);
  }, [user, featureKey, isAdmin]);

  return {
    hasAccess,
    loading,
    error,
    checkAccess,
  };
}

export interface QuotaData {
  quota: UsageQuota | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isLimitReached: boolean;
  percentageUsed: number;
}

export function useQuota(
  quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs'
): QuotaData {
  const { user, isAdmin } = useAuth();
  const [quota, setQuota] = useState<UsageQuota | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchQuota = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // ADMIN BYPASS: Admins get unlimited quota
    if (isAdmin) {
      setQuota({
        exists: true,
        used: 0,
        limit: -1,
        remaining: -1,
        percentage: 0,
        is_admin: true,
        unlimited: true,
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const quotaData = await getUsageQuota(user.id, quotaType);
      setQuota(quotaData);
    } catch (err) {
      console.error('Error fetching quota:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch quota'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuota();
  }, [user, quotaType, isAdmin]);

  // ADMIN BYPASS: Admins never reach limit
  const isLimitReached = isAdmin ? false : (quota ? quota.remaining <= 0 : false);
  const percentageUsed = isAdmin ? 0 : (quota ? quota.percentage : 0);

  return {
    quota,
    loading,
    error,
    refetch: fetchQuota,
    isLimitReached,
    percentageUsed,
  };
}

export interface TierComparisonData {
  currentTierData: SubscriptionTier | null;
  targetTierData: SubscriptionTier | null;
  isUpgrade: boolean;
  isDowngrade: boolean;
  priceDifference: number;
  newFeatures: string[];
  removedFeatures: string[];
}

export function useTierComparison(targetTier: string): TierComparisonData {
  const { tiers, currentTier } = useSubscription();
  const [comparison, setComparison] = useState<TierComparisonData>({
    currentTierData: null,
    targetTierData: null,
    isUpgrade: false,
    isDowngrade: false,
    priceDifference: 0,
    newFeatures: [],
    removedFeatures: [],
  });

  useEffect(() => {
    const currentTierData = tiers.find((t) => t.slug === currentTier) || null;
    const targetTierData = tiers.find((t) => t.slug === targetTier) || null;

    if (!currentTierData || !targetTierData) {
      return;
    }

    const isUpgrade = targetTierData.display_order > currentTierData.display_order;
    const isDowngrade = targetTierData.display_order < currentTierData.display_order;
    const priceDifference = targetTierData.monthly_price - currentTierData.monthly_price;

    const newFeatures = targetTierData.features.filter(
      (f) => !currentTierData.features.includes(f)
    );
    const removedFeatures = currentTierData.features.filter(
      (f) => !targetTierData.features.includes(f)
    );

    setComparison({
      currentTierData,
      targetTierData,
      isUpgrade,
      isDowngrade,
      priceDifference,
      newFeatures,
      removedFeatures,
    });
  }, [tiers, currentTier, targetTier]);

  return comparison;
}
